// ============================================================================
// Overall Control — Orders tab on real data (spec: docs/overall-control/ORDERS_TAB_SPEC.md).
//
// Summary blocks (two rows, columns paired as agreed), Stuck by stage with a Total column,
// Breakdown by destination / origin hub. Escalations and tech errors come from OMS (stage_extras in
// the backend). Anything whose rule is not defined yet (at risk, breached, delay, scorecard) shows
// "–", never sample data (O15).
// ============================================================================
import { Alert, Segmented, Skeleton, Table } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { getOrdersOverview } from "../../api/orders";
import { fmtWeight, STATUS_META } from "../../format";
import {
  OcRow,
  OcOrdersBreakdownRow,
  OcOrdersDimension,
  OcScopeParams,
} from "../../types/orders";
import MetricLink from "../metric_link";
import {
  AtRiskCell,
  BreachedCell,
  Cell,
  DASH,
  EscalationTeamsTable,
  OpenEscalationsCell,
  Section,
  StagesTable,
  TeamTable,
  teamsFrom,
  TechErrorsCell,
  useTabOverview,
  whenLabel,
} from "../real/common";
import OrdersListDrawer, { OrdersListRequest } from "./orders_list_drawer";
import styles from "../../index.module.scss";

interface Props {
  scope: OcScopeParams;
  rangeLabel: string; // "Today", "5 Oct", "1 Oct – 5 Oct"
  onStatus: (s: { loading: boolean; updatedAt: Date | null; refresh: () => void }) => void;
  onOpenPath: (r: OcRow) => void;
}

const OrdersTab: React.FC<Props> = ({ scope, rangeLabel, onStatus, onOpenPath }) => {
  const { data, error, loading, updatedAt, refresh } = useTabOverview(getOrdersOverview, scope, "Orders");
  const [drawer, setDrawer] = useState<OrdersListRequest | null>(null);
  const [dimension, setDimension] = useState<OcOrdersDimension>("destination");

  useEffect(() => onStatus({ loading, updatedAt, refresh }), [loading, updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  const drill = (title: string, metric: string) => () => setDrawer({ mode: "metric", title, metric, scope });
  const onDrill = (title: string, metric: string) => setDrawer({ mode: "metric", title, metric, scope });
  const when = whenLabel(rangeLabel);

  if (error && !data) return <Alert type="error" showIcon message={error} className={styles.inlineAlert} />;
  if (!data) return <Skeleton active paragraph={{ rows: 8 }} />;

  const s = data.summary;
  const next = dayjs(data.range.next_day);
  const nextLabel = next.isSame(dayjs().add(1, "day"), "day") ? "tomorrow" : `· ${next.format("D MMM")}`;

  return (
    <div>
      {error ? <Alert type="warning" showIcon message={`Showing the last loaded numbers: ${error}`} className={styles.inlineAlert} /> : null}

      <div className={styles.panel}>
        <div className={styles.summaryGrid5}>
          {/* Row 1 */}
          <Cell tone="brand" label={`Total raised ${when}`} sub={fmtWeight(s.raised.weight_kg)}>
            <MetricLink value={s.raised.count} count={s.raised.count} onClick={drill(`Total raised ${when}`, "raised")} />
          </Cell>
          <Cell tone="brand" label={`Delivery orders ${when}`} sub={fmtWeight(s.delivery.weight_kg)}>
            <MetricLink value={s.delivery.count} count={s.delivery.count} onClick={drill(`Delivery orders ${when}`, "delivery")} />
          </Cell>
          <Cell
            tone="good"
            label="Delivered %"
            sub={
              <>
                <MetricLink value={`${s.delivered.count} delivered`} count={s.delivered.count} onClick={drill("Delivered", "delivered")} />
                {" · "}
                <MetricLink value={`${s.delivered.partly} partly`} count={s.delivered.partly} onClick={drill("Partly delivered", "partly_delivered")} />
                {" · on time –"}
              </>
            }
          >
            {s.delivered.pct == null ? DASH : <span>{s.delivered.pct}%</span>}
          </Cell>
          <AtRiskCell v={s.at_risk} onDrill={onDrill} label="Delivery at risk" />
          <OpenEscalationsCell esc={s.escalations} onDrill={onDrill} />

          {/* Row 2 */}
          <Cell
            tone="warn"
            label="PO not raised"
            sub={
              <MetricLink
                value={`+${s.po_not_raised.older} older`}
                count={s.po_not_raised.older}
                onClick={drill("PO not raised · older orders", "po_not_raised_older")}
              />
            }
          >
            <MetricLink value={s.po_not_raised.count} count={s.po_not_raised.count} onClick={drill(`PO not raised ${when}`, "po_not_raised")} />
          </Cell>
          <Cell tone="info" label={`Delivery orders ${nextLabel}`} sub={fmtWeight(s.delivery_next.weight_kg)}>
            <MetricLink
              value={s.delivery_next.count}
              count={s.delivery_next.count}
              onClick={drill(`Delivery orders ${nextLabel}`, "delivery_next")}
            />
          </Cell>
          <Cell tone="violet" label="Undelivered" sub={`of ${s.delivery.count} due`}>
            <MetricLink
              value={s.undelivered.count}
              count={s.undelivered.count}
              color={STATUS_META.undelivered.text}
              onClick={drill("Undelivered", "undelivered")}
            />
          </Cell>
          <BreachedCell v={s.breached} onDrill={onDrill} label="Delivery breached" />
          <TechErrorsCell tech={s.tech_errors} onDrill={onDrill} />
        </div>
      </div>

      <Section title="Stuck by stage">
        <StagesTable stages={data.stages} onDrill={onDrill} />
      </Section>

      <Section title="Delay ownership">
        <div className={styles.sectionNote}>Delayed, weight and slip arrive with the at-risk and breach rules.</div>
        <TeamTable teams={teamsFrom(data.stages)} columns={["Delayed", "Weight", "Avg slip"]} />
        <h4 className={styles.subhead}>Escalations · by the team that resolves them</h4>
        <EscalationTeamsTable rows={data.escalation_teams} onDrill={onDrill} />
      </Section>

      <Section title="Breakdown">
        <BreakdownTable
          dimension={dimension}
          onDimension={setDimension}
          rows={data.breakdown[dimension]}
          onDrill={(title, metric) => setDrawer({ mode: "metric", title, metric, scope })}
        />
      </Section>

      <Section title="Stage owner scorecard">
        <div className={styles.sectionNote}>Coming later.</div>
        <TeamTable
          teams={teamsFrom(data.stages)}
          columns={["Completed", "% within SLA", "Stuck with them now", "Delays they started", "Open esc."]}
        />
      </Section>

      <OrdersListDrawer request={drawer} onClose={() => setDrawer(null)} onOpenPath={onOpenPath} />
    </div>
  );
};

const BreakdownTable: React.FC<{
  dimension: OcOrdersDimension;
  onDimension: (d: OcOrdersDimension) => void;
  rows: OcOrdersBreakdownRow[];
  onDrill: (title: string, metric: string) => void;
}> = ({ dimension, onDimension, rows, onDrill }) => {
  const m = (part: string, label: string) => (v: number, r: OcOrdersBreakdownRow) =>
    <MetricLink value={v} count={v} onClick={() => onDrill(`${r.label} · ${label}`, `bd:${dimension}:${r.key}:${part}`)} />;
  const pending = { align: "right" as const, width: 90, render: () => DASH };
  const columns: ColumnsType<OcOrdersBreakdownRow> = [
    {
      title: dimension === "destination" ? "Destination" : "Origin hub",
      dataIndex: "label",
      render: (v: string) => <span className={styles.strong}>{v}</span>,
    },
    { title: "Orders", dataIndex: "total", align: "right", width: 90, render: m("total", "orders") },
    { title: "Weight", dataIndex: "weight_kg", align: "right", width: 90, render: (v: number) => fmtWeight(v) },
    { title: "Delivered %", dataIndex: "delivered_pct", align: "right", width: 100, render: (v: number | null) => (v == null ? DASH : `${v}%`) },
    { title: "Delivered", dataIndex: "delivered", align: "right", width: 90, render: m("delivered", "delivered") },
    { title: "Undelivered", dataIndex: "undelivered", align: "right", width: 100, render: m("undelivered", "undelivered") },
    { title: "PO not raised", dataIndex: "po_not_raised", align: "right", width: 110, render: m("po_not_raised", "PO not raised") },
    { title: "At risk", key: "at_risk", ...pending },
    { title: "Breached", key: "breached", ...pending },
    { title: "Open esc.", key: "open_escalations", ...pending },
  ];
  return (
    <div>
      <div className={styles.sectionToolbar}>
        <Segmented
          size="small"
          value={dimension}
          onChange={(v) => onDimension(v as OcOrdersDimension)}
          options={[
            { label: "Destination", value: "destination" },
            { label: "Origin hub", value: "origin_hub" },
          ]}
        />
        <span className={styles.muted}>Orders due for delivery in the range.</span>
      </div>
      <Table<OcOrdersBreakdownRow>
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        className={styles.denseTable}
        scroll={{ x: 1000 }}
        locale={{ emptyText: "No orders due in this range." }}
      />
    </div>
  );
};

export default OrdersTab;
